<template>
    <!--
      Real-image thumb: wrapped in a hover target that drives a
      JS-positioned zoom overlay (see onThumbMouseEnter). The overlay is
      ``position: fixed`` so it escapes any scrollable ancestor -- a
      pure-CSS :hover reveal would be clipped by a scroll container (the
      #2389 fix). Placeholder rendering (no imageUrl) gets no
      wrapper/overlay/listeners.
    -->
    <span class="tf-thumb" :style="frameStyle">
        <span
            v-if="imageUrl"
            class="tf-thumb__wrap"
            @mouseenter="onThumbMouseEnter($event)"
            @mouseleave="onThumbMouseLeave"
        >
            <img
                :src="imageUrl"
                :alt="alt"
                class="tf-thumb__img"
                :data-testid="imgTestid"
            />
            <span
                v-if="hovered"
                class="tf-thumb__zoom"
                :data-testid="zoomTestidComputed"
                :style="zoomStyle"
            >
                <img :src="imageUrl" :alt="alt" />
            </span>
        </span>
        <span
            v-else
            class="tf-thumb__placeholder"
            :data-testid="placeholderTestidComputed"
        ></span>
    </span>
</template>

<script>
/**
 * ProductThumbHoverZoom -- a reusable product thumbnail with a JS-positioned,
 * viewport-clamped hover-zoom overlay.
 *
 * Product previews appear all over the app (category cards, decoration-library
 * "where it's used" rows, ...). The thumbnail + hover-zoom mechanism was
 * duplicated inline; this component owns it once so call sites just pass an
 * ``imageUrl`` and sizing.
 *
 * The overlay is measured from the thumb's getBoundingClientRect() and
 * rendered ``position: fixed`` so it escapes any scrollable ancestor rather
 * than being clipped by it (the #2389 viewport-clip fix). It flips to the left
 * of the thumb when there is no room on the right, and clamps on both axes so
 * the whole box stays within the viewport. ``pointer-events: none`` keeps any
 * link the component sits inside clickable underneath.
 *
 * Placeholder rendering (``imageUrl`` null) gets NO hover wrapper, listeners,
 * or overlay -- there is nothing to zoom.
 */
export default {
    name: 'ProductThumbHoverZoom',
    props: {
        // The thumbnail URL. Null/empty => render the placeholder box (no
        // hover affordance).
        imageUrl: {
            type: String,
            default: null,
        },
        alt: {
            type: String,
            default: '',
        },
        // Zoom overlay edge length (px). Both call sites can match their
        // sizing via this + thumbSize.
        zoomSize: {
            type: Number,
            default: 224,
        },
        // Thumbnail edge length (px).
        thumbSize: {
            type: Number,
            default: 56,
        },
        // data-testid support. Callers can either pass a ``testidBase`` + ``id``
        // (the component derives ``<base>-<id>``, ``<base>-zoom-<id>``,
        // ``<base>-placeholder-<id>``) or pass the three explicit testids
        // directly. Explicit values win when provided.
        testidBase: {
            type: String,
            default: null,
        },
        id: {
            type: [String, Number],
            default: null,
        },
        testid: {
            type: String,
            default: null,
        },
        zoomTestid: {
            type: String,
            default: null,
        },
        placeholderTestid: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            // Whether the zoom overlay is currently shown (this component's
            // own hover state -- not a parent's).
            hovered: false,
            zoomStyle: {},
        }
    },
    computed: {
        frameStyle() {
            return {
                width: `${this.thumbSize}px`,
                height: `${this.thumbSize}px`,
            }
        },
        // ``<base>-<id>`` when a base+id are provided; an explicit ``testid``
        // overrides.
        imgTestid() {
            if (this.testid) return this.testid
            if (this.testidBase != null && this.id != null) {
                return `${this.testidBase}-${this.id}`
            }
            return undefined
        },
        zoomTestidComputed() {
            if (this.zoomTestid) return this.zoomTestid
            if (this.testidBase != null && this.id != null) {
                return `${this.testidBase}-zoom-${this.id}`
            }
            return undefined
        },
        placeholderTestidComputed() {
            if (this.placeholderTestid) return this.placeholderTestid
            if (this.testidBase != null && this.id != null) {
                return `${this.testidBase}-placeholder-${this.id}`
            }
            return undefined
        },
    },
    mounted() {
        // Hover-zoom is a transient preview -- on scroll (this component's
        // section, the page, or any scrollable ancestor), just hide it rather
        // than tracking/repositioning; ``capture: true`` catches scroll on any
        // scrollable ancestor, not just window.
        window.addEventListener('scroll', this.onWindowScroll, true)
    },
    unmounted() {
        window.removeEventListener('scroll', this.onWindowScroll, true)
    },
    methods: {
        onThumbMouseEnter(event) {
            const wrap = event.currentTarget
            if (!wrap || typeof wrap.getBoundingClientRect !== 'function')
                return
            const rect = wrap.getBoundingClientRect()
            const ZOOM_SIZE = this.zoomSize
            const GAP = 8

            let left = rect.right + GAP
            if (left + ZOOM_SIZE > window.innerWidth) {
                // No room on the right -- flip to the left of the thumb.
                left = rect.left - GAP - ZOOM_SIZE
            }
            // Last-resort horizontal clamp (very narrow viewport where
            // neither side fully fits) so it never renders off the left.
            left = Math.max(0, left)

            // Align with the thumb's top by default, then clamp so the
            // whole ZOOM_SIZE box stays within [0, innerHeight].
            let top = rect.top
            if (top + ZOOM_SIZE > window.innerHeight) {
                top = window.innerHeight - ZOOM_SIZE
            }
            top = Math.max(0, top)

            this.zoomStyle = {
                position: 'fixed',
                left: `${left}px`,
                top: `${top}px`,
                width: `${ZOOM_SIZE}px`,
                height: `${ZOOM_SIZE}px`,
                pointerEvents: 'none',
            }
            this.hovered = true
        },

        onThumbMouseLeave() {
            this.hovered = false
        },

        // Transient hover preview -- a scroll while hovering just hides it
        // rather than tracking/repositioning, matching the mouseleave
        // contract.
        onWindowScroll() {
            if (this.hovered) {
                this.hovered = false
            }
        },
    },
}
</script>

<style scoped>
.tf-thumb {
    flex: 0 0 auto;
    border-radius: 6px;
    overflow: visible;
    background: var(--tf-surface-2, #f5f5f5);
    border: 1px solid var(--tf-border, #e0e0e0);
    display: flex;
    align-items: center;
    justify-content: center;
}
/* Hover target for the zoom overlay's mouseenter/mouseleave handlers --
   no ``position: relative`` needed since the overlay is ``position: fixed``
   (viewport-relative, JS-positioned from this wrap's
   getBoundingClientRect()), not positioned relative to this wrapper. */
.tf-thumb__wrap {
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    overflow: hidden;
}
.tf-thumb__img {
    width: 100%;
    height: 100%;
    /* Product photos are non-square (full garment on a white field) --
       ``contain`` shows the WHOLE garment; ``cover`` cropped it. The
       neutral bg fills the letterboxed edges. */
    object-fit: contain;
    display: block;
}
.tf-thumb__placeholder {
    width: 100%;
    height: 100%;
    display: block;
    background: var(--tf-surface-2, #f5f5f5);
}
/* JS-positioned hover-zoom overlay (see onThumbMouseEnter). Rendered
   ``position: fixed`` so it escapes any scrollable ancestor entirely -- a
   CSS-only absolute overlay would be clipped (the #2389 bug). The fixed
   position + width/height come from the inline zoomStyle; ``pointer-events:
   none`` keeps any link underneath clickable. */
.tf-thumb__zoom {
    z-index: 40;
    padding: 8px;
    border-radius: var(--tf-radius-lg, 12px);
    border: 1px solid var(--tf-border, #e0e0e0);
    background: #fff;
    box-shadow: 0 8px 24px rgba(20, 40, 60, 0.18);
    pointer-events: none;
}
.tf-thumb__zoom img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}
</style>
